/**
 * cacheStore — the data-cache engine for the new app.
 *
 * Replaces src/services/cacheService.js, which was hard-disabled
 * (getCacheEnabled() literally `return false`) because it had three bugs
 * that make a cache worse than no cache at all:
 *
 *   1. No per-user namespace. The API filters rows by the caller's role,
 *      so `/students` returns a DIFFERENT list for an admin and for a
 *      teacher. One shared key meant the second person to sign in on the
 *      same machine could be served the first person's rows. That is a
 *      data-leak, not a performance bug — hence `scope()` below.
 *   2. It mirrored EVERY entry into localStorage. The student directory
 *      alone is 692 rows with phones, attendance and balances; a couple
 *      of those blow past the ~5MB origin quota, and the old code caught
 *      the QuotaExceededError with a console.warn and then kept writing —
 *      so after the first overflow nothing persisted and every write
 *      still paid a full JSON.stringify of the whole cache.
 *   3. It read/wrote the entire `apiCache` blob on every single set().
 *      O(n) serialisation per cache write.
 *
 * The shape here instead:
 *
 *   memory  — a Map. Always written. Lost on reload. Unlimited-ish.
 *   disk    — localStorage, ONE key per entry, and only for entries the
 *             caller explicitly marks `persist: true`. This is what makes
 *             the "instant on cold start / works offline" promise real
 *             without risking the quota: we persist the small, slow-
 *             changing reference data (grades, subjects, sections,
 *             teachers) and keep the big volatile lists memory-only.
 *
 * Everything that touches localStorage is wrapped in try/catch: Safari
 * private mode throws on *read* as well as write, and an exception here
 * would take down the whole page render.
 */

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

/** Bump this when the stored entry shape changes. Old-format entries are
 *  then ignored and swept, instead of being handed to code that expects
 *  new fields (the classic "cache outlives the deploy" crash). */
const SCHEMA = 'v2';

/** Every persisted entry is a separate localStorage key under this
 *  prefix, so eviction and clearing are cheap scans rather than a
 *  read-modify-write of one giant JSON blob. */
const DISK_PREFIX = `mtz.cache.${SCHEMA}.`;

/** The user-choice flag (stale-while-revalidate vs live-only). Lives
 *  outside DISK_PREFIX so clearing the cache never clears the setting. */
const MODE_KEY = 'mtz.cache.mode';

/** The session user written by ui/api.js `session.save`. Read directly
 *  rather than through AuthContext because the store is a plain module
 *  used from non-React code (invalidate() after a save, logout, etc.). */
const USER_KEY = 'mtzedu_session_user';

/** Rough cap on what we're willing to keep on disk. localStorage is
 *  ~5MB per origin and the app already stores the session there, so we
 *  stay well under and leave room. Measured in UTF-16 chars (~2 bytes
 *  each in the worst case), so 1.5M chars ≈ 3MB worst case. */
const DISK_BUDGET_CHARS = 1_500_000;

/** A single entry bigger than this is never persisted at all. The
 *  student directory serialises to well over a megabyte; writing it
 *  would evict everything else and still probably fail. Memory-only is
 *  the correct answer for those. */
const MAX_ENTRY_CHARS = 180_000;

export const MODE_SWR = 'swr';   // stale-while-revalidate (default)
export const MODE_LIVE = 'live'; // مباشر فقط — never read cache

// ---------------------------------------------------------------------
// TTL catalogue — structured by VOLATILITY, not by page.
//
// The rule that drives these numbers: a stale value is acceptable only
// when acting on it cannot cause a real-world mistake. Money and
// attendance fail that test, so they get short or zero TTLs even though
// they are the most expensive requests. A teacher marking a student
// present twice, or an office telling a parent a balance that was
// settled an hour ago, is worse than a 400ms spinner.
// ---------------------------------------------------------------------
export const TTL = {
  /** School structure: edited a handful of times per YEAR (grades,
   *  subjects, batches, academic years, exam types, price profiles).
   *  Safe to serve stale and safe to persist — this is the data that
   *  makes the app usable with no connection. */
  STATIC: 24 * 60 * 60 * 1000,      // 24h

  /** Roster-shaped data: sections, teachers, transportation, users,
   *  families. Changes a few times per term. Long enough to feel
   *  instant all day, short enough that a new teacher shows up the same
   *  session if you leave the tab open. */
  REFERENCE: 60 * 60 * 1000,        // 1h

  /** Student lists and enrollments. Edited during the day by the office,
   *  but a 5-minute-old list never causes a wrong decision — and SWR
   *  means the fresh copy lands a moment after the stale one paints. */
  ROSTER: 5 * 60 * 1000,            // 5m

  /** Exam definitions and published results: written in bursts, read a
   *  lot, and a stale mark is visible/checkable by the user. */
  RESULTS: 2 * 60 * 1000,           // 2m

  /** Dashboard counters. Wrong-by-a-bit is tolerable; they are never
   *  acted on directly. */
  SUMMARY: 60 * 1000,               // 1m

  /** Anything where stale data causes a real mistake: today's roster
   *  while attendance is being taken, payment balances, notifications.
   *  Cached only long enough to survive a tab switch / modal reopen, and
   *  NEVER persisted. See NEVER_PERSIST below. */
  VOLATILE: 20 * 1000,              // 20s

  /** Explicit "do not cache". Used for auth and for anything a mutation
   *  is about to touch. */
  NONE: 0,
};

/**
 * Key fragments that must never reach localStorage even if a caller
 * passes `persist: true`. Attendance and money survive in memory for the
 * length of the tab, but a browser restart must go back to the server —
 * we never want yesterday's balance rendering instantly as if it were
 * today's.
 */
const NEVER_PERSIST = ['attendance', 'roster', 'payment', 'balance', 'notification'];

/**
 * Endpoints that must never be cached in any tier. The auth handshake is
 * one-shot by design: login-lookup returns candidate identities,
 * login-select triggers an SMS, login-verify burns a code. Replaying any
 * of them from cache is at best broken and at worst a way to show one
 * person the list of accounts another person looked up.
 */
const NEVER_CACHE = ['/auth/', 'login-lookup', 'login-select', 'login-verify', 'logout'];

/** True when this key must bypass the cache entirely. */
export function isCacheable(key) {
  if (!key) return false;
  const k = String(key).toLowerCase();
  return !NEVER_CACHE.some((frag) => k.includes(frag));
}

function persistable(key) {
  const k = String(key).toLowerCase();
  return !NEVER_PERSIST.some((frag) => k.includes(frag));
}

// ---------------------------------------------------------------------
// Safe storage helpers. localStorage throws in private mode, when the
// user has blocked site data, and (on write) when the quota is full.
// Every access goes through these four.
// ---------------------------------------------------------------------

function lsGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}

function lsSet(key, value) {
  try { localStorage.setItem(key, value); return true; } catch { return false; }
}

function lsRemove(key) {
  try { localStorage.removeItem(key); } catch { /* nothing we can do */ }
}

/** All of OUR keys currently on disk. Wrapped because even enumerating
 *  localStorage.length throws when storage is blocked. */
function diskKeys() {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DISK_PREFIX)) out.push(k);
    }
  } catch { /* storage unavailable — behave as memory-only */ }
  return out;
}

// ---------------------------------------------------------------------
// Per-user scoping
// ---------------------------------------------------------------------

/**
 * The namespace every key is prefixed with.
 *
 * SECURITY: this is the single most important line in the file. The API
 * filters by the bearer token's role, so the same URL yields different
 * rows per user. Without the user id in the key, signing out and signing
 * in as someone else on the same machine would serve the previous
 * person's rows from memory/disk with no request made at all.
 *
 * Read fresh on every call rather than captured once at module load:
 * the module is imported before login happens, so a captured value would
 * be `anon` for the whole session.
 */
export function scope() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return 'anon';
    const u = JSON.parse(raw);
    // Role is included as well as id. It costs nothing and it means that
    // if the backend ever changes what a role can see, or a user's role
    // is changed mid-session, the old rows simply become unreachable
    // instead of being shown to a now-differently-privileged session.
    return u && u.id ? `u${u.id}.${u.role || '?'}` : 'anon';
  } catch {
    return 'anon';
  }
}

/** The full storage key for a logical cache key. */
function full(key) {
  return `${scope()}|${key}`;
}

// ---------------------------------------------------------------------
// The store
// ---------------------------------------------------------------------

/** key -> { data, at, ttl } where `at` is the time the data was fetched. */
const memory = new Map();

/** Subscribers to mode changes + cache mutations, so the toggle and any
 *  mounted useCachedData re-evaluate together. */
const listeners = new Set();

function emit() {
  // A listener throwing must not stop the others from being told.
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

/** Subscribe to cache/mode changes. Returns an unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// --- mode (the navbar toggle) ----------------------------------------

let mode = (() => {
  const saved = lsGet(MODE_KEY);
  return saved === MODE_LIVE ? MODE_LIVE : MODE_SWR; // default ON
})();

export function getMode() { return mode; }
export function isCacheEnabled() { return mode === MODE_SWR; }

export function setMode(next) {
  const value = next === MODE_LIVE ? MODE_LIVE : MODE_SWR;
  if (value === mode) return mode;
  mode = value;
  lsSet(MODE_KEY, value);
  // Switching to live-only drops everything held. If the user distrusts
  // the cache enough to turn it off, leaving a warm copy around to be
  // served the moment they turn it back on defeats the point — and this
  // is also the "get me out of here, something looks wrong" button.
  if (value === MODE_LIVE) clear();
  emit();
  return mode;
}

export function toggleMode() {
  return setMode(mode === MODE_SWR ? MODE_LIVE : MODE_SWR);
}

// --- disk tier --------------------------------------------------------

/** Current approximate size of our disk footprint, in UTF-16 chars. */
function diskSize() {
  let total = 0;
  diskKeys().forEach((k) => {
    const v = lsGet(k);
    if (v) total += v.length + k.length;
  });
  return total;
}

/**
 * Evict oldest-first until `need` chars are free (or nothing is left).
 *
 * "Oldest" is by fetch time, which is the right axis for a cache whose
 * entries all have TTLs: the oldest entry is the closest to useless.
 * Expired entries are dropped first regardless of size, since they are
 * free wins.
 */
function evict(need) {
  const keys = diskKeys();
  const now = Date.now();
  const entries = [];

  keys.forEach((k) => {
    const raw = lsGet(k);
    if (!raw) return;
    let parsed;
    try { parsed = JSON.parse(raw); } catch { lsRemove(k); return; }
    if (!parsed || typeof parsed.at !== 'number') { lsRemove(k); return; }
    // Free win: already expired.
    if (parsed.ttl && now - parsed.at > parsed.ttl) { lsRemove(k); return; }
    entries.push({ k, at: parsed.at, size: raw.length + k.length });
  });

  let freed = 0;
  entries.sort((a, b) => a.at - b.at);
  for (const e of entries) {
    if (freed >= need) break;
    lsRemove(e.k);
    freed += e.size;
  }
  return freed;
}

function diskWrite(key, entry) {
  if (!persistable(key)) return;

  let raw;
  try {
    raw = JSON.stringify(entry);
  } catch {
    // Circular or non-serialisable payload (a Date is fine, a DOM node
    // is not). Memory tier already holds it; disk just skips it.
    return;
  }

  // Oversized single entry: memory-only by design, not an error.
  if (raw.length > MAX_ENTRY_CHARS) return;

  const storageKey = DISK_PREFIX + full(key);

  // Pre-emptive eviction: cheaper and far more predictable than waiting
  // for the browser to throw. We only pay the diskSize() scan when we
  // are actually about to persist something, which is the minority of
  // writes (most entries are memory-only).
  const size = diskSize();
  if (size + raw.length > DISK_BUDGET_CHARS) {
    evict(size + raw.length - DISK_BUDGET_CHARS);
  }

  if (!lsSet(storageKey, raw)) {
    // QuotaExceededError despite the budget check (other code shares this
    // origin's quota, and Safari's accounting is its own thing). Evict
    // aggressively — a quarter of the budget — and try exactly once more.
    // If it still fails we give up silently: a missing disk copy is a
    // slower app, never a broken one.
    evict(DISK_BUDGET_CHARS / 4);
    lsSet(storageKey, raw);
  }
}

function diskRead(key) {
  const raw = lsGet(DISK_PREFIX + full(key));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.at !== 'number') return null;
    return parsed;
  } catch {
    // Corrupt entry (a half-written value from a killed tab). Drop it.
    lsRemove(DISK_PREFIX + full(key));
    return null;
  }
}

// --- read / write -----------------------------------------------------

/**
 * Read an entry without applying the TTL.
 *
 * Returns `{ data, at, age, stale }` or null. Callers need the stale
 * flag rather than a bare hit/miss because stale-while-revalidate
 * deliberately USES expired data: it paints it immediately and replaces
 * it when the network answers. An offline caller uses it too, at any
 * age, rather than showing an error.
 */
export function peek(key) {
  if (!key || !isCacheable(key)) return null;

  const fk = full(key);
  let entry = memory.get(fk);

  if (!entry) {
    const fromDisk = diskRead(key);
    if (fromDisk) {
      // Promote to memory so the next read in this session is free.
      memory.set(fk, fromDisk);
      entry = fromDisk;
    }
  }

  if (!entry) return null;

  const age = Date.now() - entry.at;
  return {
    data: entry.data,
    at: entry.at,
    age,
    stale: entry.ttl > 0 ? age > entry.ttl : true,
  };
}

/**
 * Read an entry, honouring the TTL and the mode. Returns the data or
 * null. This is the "is there a usable fresh copy?" question.
 */
export function get(key) {
  if (!isCacheEnabled()) return null;
  const hit = peek(key);
  if (!hit || hit.stale) return null;
  return hit.data;
}

/**
 * Store a value.
 *
 * @param {string}  key                 logical cache key (no user prefix)
 * @param {*}       data                the payload
 * @param {object} [opts]
 * @param {number} [opts.ttl=TTL.ROSTER]
 * @param {boolean}[opts.persist=false] also write to localStorage
 *
 * A ttl of 0 (TTL.NONE) is a no-op: it is how a caller says "this one is
 * not cacheable" without branching at the call site.
 */
export function set(key, data, { ttl = TTL.ROSTER, persist = false } = {}) {
  if (!key || !isCacheEnabled() || !isCacheable(key) || !ttl) return;

  const entry = { data, at: Date.now(), ttl };
  memory.set(full(key), entry);
  if (persist) diskWrite(key, entry);
}

/** Drop one exact key from both tiers. */
export function remove(key) {
  memory.delete(full(key));
  lsRemove(DISK_PREFIX + full(key));
}

// --- invalidation -----------------------------------------------------

/**
 * Invalidate everything whose key contains `pattern` (a substring) or
 * matches it (a RegExp), for the CURRENT user only.
 *
 * This is the half of caching that actually goes wrong in production.
 * The rule for pages: after any successful mutation, invalidate the
 * nouns the mutation touched, then reload. Substring matching is
 * deliberate — `invalidate('students')` catches `students`,
 * `students?grade=3`, `student:41` and `student-directory:2026-09-23`
 * in one call, which is what you want and what a page author will
 * actually remember to write.
 *
 * Scoped to the current user so one person's save cannot flush another
 * account's warm cache out from under them in a shared-browser setup.
 *
 * @returns {number} how many entries were dropped
 */
export function invalidate(pattern) {
  if (!pattern) return 0;
  const prefix = `${scope()}|`;
  const test = pattern instanceof RegExp
    ? (k) => pattern.test(k)
    : (k) => k.includes(String(pattern));

  let n = 0;

  for (const fk of [...memory.keys()]) {
    if (!fk.startsWith(prefix)) continue;
    if (test(fk.slice(prefix.length))) { memory.delete(fk); n += 1; }
  }

  diskKeys().forEach((sk) => {
    const logical = sk.slice(DISK_PREFIX.length);
    if (!logical.startsWith(prefix)) return;
    if (test(logical.slice(prefix.length))) { lsRemove(sk); n += 1; }
  });

  if (n) emit();
  return n;
}

/**
 * Invalidate several patterns at once and notify subscribers once.
 * `invalidateAll(['students', 'sections'])` after a transfer.
 */
export function invalidateAll(patterns = []) {
  return patterns.reduce((n, p) => n + invalidate(p), 0);
}

/**
 * Nuke everything, for every user, in both tiers.
 *
 * MUST be called on login AND on logout:
 *   - on logout, so the next person cannot read the previous person's
 *     data out of memory (same tab) or disk (same machine);
 *   - on login, because a session can also start without a logout ever
 *     running — the API client calls session.clear() + reload on a 401,
 *     and people close tabs. Clearing at the start of a session is the
 *     only point that is guaranteed to happen.
 *
 * Only OUR keys are removed; localStorage.clear() would also wipe the
 * session token this app just wrote (the old service's clearAllStorage
 * did exactly that).
 */
export function clear() {
  memory.clear();
  diskKeys().forEach(lsRemove);
  emit();
}

/**
 * Sweep expired entries off disk. Cheap, and worth doing once at
 * startup so a machine that was offline for a week doesn't carry a
 * fully-expired 3MB of junk around.
 */
export function sweep() {
  const now = Date.now();
  diskKeys().forEach((k) => {
    const raw = lsGet(k);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.at !== 'number') { lsRemove(k); return; }
      if (parsed.ttl && now - parsed.at > parsed.ttl) lsRemove(k);
    } catch {
      lsRemove(k);
    }
  });

  // Entries from an older SCHEMA can never be read again — remove them
  // so they don't occupy quota forever after a deploy.
  try {
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith('mtz.cache.') && !k.startsWith(DISK_PREFIX) && k !== MODE_KEY && k !== VERSION_KEY) {
        lsRemove(k);
      }
    }
  } catch { /* storage unavailable */ }
}

/** Diagnostics for the toggle's tooltip / a debug screen. */
export function stats() {
  return {
    mode,
    enabled: isCacheEnabled(),
    memoryEntries: memory.size,
    diskEntries: diskKeys().length,
    diskChars: diskSize(),
    diskBudget: DISK_BUDGET_CHARS,
    scope: scope(),
  };
}

// ---------------------------------------------------------------------
// Data version — "has the database changed since this was cached?"
//
// A TTL alone cannot answer that: a list cached a minute ago is "fresh"
// by its TTL even if the office added a student thirty seconds ago. So
// the server keeps one number that moves on every write
// (api/src/lib/dataVersion.js), and this cache is only trusted while
// its number still matches:
//
//   • our own write     → the response carries the new number → the
//                         cache is dropped at once (api.js request()).
//   • someone else's    → before serving a cached entry, useCachedData
//                         asks the server for the number (one tiny row,
//                         at most once per VERSION_TRUST_MS); different
//                         → drop everything and fetch fresh.
//   • a screen left open → re-checked on focus/tab-return and every
//                         VERSION_POLL_MS, so open screens refresh too.
//
// Offline, the check fails and the cache is used as before — that is
// exactly when it is needed.
// ---------------------------------------------------------------------
const VERSION_KEY = 'mtz.cache.dataVersion';
// Only long enough to share ONE check between the several hooks that
// mount together on a page. It was 10s, and a change made by someone
// else in those 10s was served stale on the next page open — exactly
// the case this whole mechanism exists for. The check is one tiny row.
const VERSION_TRUST_MS = 1500;
const VERSION_POLL_MS = 60 * 1000;

let knownVersion = lsGet(VERSION_KEY);
let confirmedAt = 0;
let versionFetcher = null;
let versionInflight = null;
let watching = false;

/** Record the server's current data version. Returns true when it had
 *  moved (and the cache was therefore dropped). */
export function noteDataVersion(v) {
  if (v == null || v === '') return false;
  const next = String(v);
  confirmedAt = Date.now();
  if (next === knownVersion) return false;
  knownVersion = next;
  lsSet(VERSION_KEY, next);
  const hadEntries = memory.size > 0 || diskKeys().length > 0;
  memory.clear();
  diskKeys().forEach(lsRemove);
  // Subscribed screens refetch in the background on emit().
  if (hadEntries) emit();
  return true;
}

/** Confirmed against the server within the last few seconds? */
export function isVersionFresh() {
  return Date.now() - confirmedAt < VERSION_TRUST_MS;
}

/** Ask the server for its version (deduplicated). Resolves to true if
 *  the cache was dropped. Never rejects. */
export function checkDataVersion({ force = false } = {}) {
  if (!versionFetcher || !isCacheEnabled()) return Promise.resolve(false);
  if (!force && isVersionFresh()) return Promise.resolve(false);
  if (!versionInflight) {
    versionInflight = Promise.resolve()
      .then(() => versionFetcher())
      .then((v) => noteDataVersion(v))
      .catch(() => false)
      .finally(() => { versionInflight = null; });
  }
  return versionInflight;
}

function startVersionWatch() {
  if (watching || typeof window === 'undefined') return;
  watching = true;
  const recheck = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    checkDataVersion({ force: true });
  };
  window.addEventListener('focus', recheck);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', recheck);
  setInterval(recheck, VERSION_POLL_MS);
}

/** Wired once by ui/api.js — kept as an injection so this module never
 *  imports the API client (which imports this module). */
export function setVersionFetcher(fn) {
  versionFetcher = fn;
  startVersionWatch();
}

// Run one sweep per page load. Guarded so it never breaks a render if
// storage is unavailable.
try { sweep(); } catch { /* ignore */ }

const cacheStore = {
  TTL,
  MODE_SWR,
  MODE_LIVE,
  get,
  set,
  peek,
  remove,
  invalidate,
  invalidateAll,
  clear,
  sweep,
  stats,
  scope,
  subscribe,
  getMode,
  setMode,
  toggleMode,
  isCacheEnabled,
  isCacheable,
  noteDataVersion,
  checkDataVersion,
  isVersionFresh,
};

export default cacheStore;
